import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { getMe } from 'service/AdminPanelService'
import { setUser, setLoading, clearUser } from 'store/slices/AuthSlice'
import UnvLayout from 'components/Layouts/UnvLayout/UnvLayout'
import { Spinner } from 'components/UnvUI'
import tokenDecode from 'utils/decode'

const readToken = () => {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem('token')
  } catch (_) {
    return null
  }
}

function ProtectedRoute({ children, requireRoles, bare }) {
  const dispatch = useDispatch()
  const auth = useSelector((state) => state.auth)

  // Token из state, чтобы реагировать на изменения (logout в другой вкладке).
  const [token, setToken] = useState(() => readToken())

  // Подписка на storage event — logout в другой вкладке должен выкинуть и тут.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const onStorage = (e) => {
      if (e.key === 'token' || e.key === null) {
        setToken(readToken())
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // JWT-валидация: decode.js возвращает null при истёкшем exp / битом токене.
  const tokenValid = useMemo(() => {
    if (!token) return false
    return tokenDecode(token) !== null
  }, [token])

  const fetchMe = useCallback(() => {
    dispatch(setLoading(true))
    getMe()
      .then((res) => {
        dispatch(setUser(res?.data || res))
      })
      .catch(() => {
        // Ошибка получения /me — выкидываем пользователя из системы.
        dispatch(clearUser())
        dispatch(setLoading(false))
        try {
          localStorage.removeItem('token')
          setToken(null)
        } catch (_) {
          /* ignore */
        }
      })
  }, [dispatch])

  useEffect(() => {
    if (!tokenValid) return
    if (auth?.loaded || auth?.loading) return
    fetchMe()
  }, [tokenValid, auth?.loaded, auth?.loading, fetchMe])

  // Истёкший/битый токен — чистим.
  if (!token || !tokenValid) {
    if (token && !tokenValid) {
      try {
        localStorage.removeItem('token')
      } catch (_) {
        /* ignore */
      }
    }
    return <Navigate to="/login" replace />
  }

  // Пока подгружается профиль — показываем spinner вместо пустого экрана.
  if (auth?.loading || (!auth?.loaded && !auth?.me)) {
    return <Spinner.Center />
  }

  // is_locked из MeView — отправляем на страницу разблокировки.
  if (auth?.loaded && (auth?.isLocked || auth?.me?.is_locked)) {
    return <Navigate to="/account-locked/" replace />
  }

  if (Array.isArray(requireRoles) && requireRoles.length > 0) {
    if (!auth?.loaded) {
      return <Spinner.Center />
    }
    const isSuperuser = !!(auth?.me?.is_superuser)
    const userRoleCodes = (auth?.roles || []).map((r) =>
      typeof r === 'string' ? r : r?.code,
    )
    const allowed =
      isSuperuser || requireRoles.some((r) => userRoleCodes.includes(r))
    if (!allowed) {
      // Если у пользователя есть хоть какая-то роль — отправляем на /home/.
      // Иначе на /login/, чтобы не было бесконечных редиректов.
      const target = userRoleCodes.length > 0 ? '/home/' : '/login'
      return <Navigate to={target} replace />
    }
  }

  // По умолчанию заворачиваем страницу в общий шелл (sidebar + topbar).
  // bare=true оставляет страницу без обёртки (для full-screen экранов
  // вроде /post-login/ или /2fa-setup/).
  if (bare) return children
  return <UnvLayout>{children}</UnvLayout>
}

export default ProtectedRoute
